import {
  BehaviorSignalSettings,
  RecordingFeatures,
  AutomaticImageUnmaskMode,
  KeepRecordingType,
  GetDetectionsConfigurationRulesResponse,
} from '@fullstory/proto/fs/services/ingestion-settings';
import { createRequestReducer } from '@fullstory/redux-util';
import { combineReducers } from '@fullstory/redux-util/utils';
import { IngestionSettingsTypes, KeepRuleSetWithId } from './types';
import { IngestionSettingsActions } from './actions';
import {
  CanvasWatcherMode,
  RecordingActiveFor,
} from '@fullstory/proto/fs/api/fullstoryapis/settings/recording/v1alpha';
import { NamedElementsActions } from '../namedElements';
import { NamedElementsRequestTypes } from '../namedElements/types';

type BehaviorSignalSettingsState = {
  loading: boolean;
  error: boolean;
  data: BehaviorSignalSettings | null;
};

const behaviorSignalSettings = combineReducers<BehaviorSignalSettingsState>({
  loading: function loading(state: boolean = false, action: IngestionSettingsActions): boolean {
    switch (action.type) {
      case IngestionSettingsTypes.FetchBehaviorSignalSettingsRequest:
        return true;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsRequest:
        return true;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsRequest:
        return true;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsRequest:
        return true;

      case IngestionSettingsTypes.FetchBehaviorSignalSettingsSuccess:
        return false;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsSuccess:
        return false;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsSuccess:
        return false;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsSuccess:
        return false;

      case IngestionSettingsTypes.FetchBehaviorSignalSettingsFailure:
        return false;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsFailure:
        return false;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsFailure:
        return false;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsFailure:
        return false;
    }

    return state;
  },
  error: function error(state: boolean = false, action: IngestionSettingsActions): boolean {
    switch (action.type) {
      case IngestionSettingsTypes.FetchBehaviorSignalSettingsRequest:
        return false;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsRequest:
        return false;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsRequest:
        return false;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsRequest:
        return false;

      case IngestionSettingsTypes.FetchBehaviorSignalSettingsSuccess:
        return false;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsSuccess:
        return false;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsSuccess:
        return false;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsSuccess:
        return false;

      case IngestionSettingsTypes.FetchBehaviorSignalSettingsFailure:
        return true;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsFailure:
        return true;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsFailure:
        return true;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsFailure:
        return true;
    }

    return state;
  },
  data: function data(
    state: BehaviorSignalSettings | null = null,
    action: IngestionSettingsActions | NamedElementsActions,
  ): BehaviorSignalSettings | null {
    switch (action.type) {
      case IngestionSettingsTypes.FetchBehaviorSignalSettingsSuccess:
        return action.data;
      case IngestionSettingsTypes.CreateBehaviorSignalSettingsSuccess:
        return action.data;
      case IngestionSettingsTypes.DeleteBehaviorSignalSettingsSuccess:
        return action.data;
      case IngestionSettingsTypes.UpdateBehaviorSignalSettingsSuccess:
        return action.data;
      case NamedElementsRequestTypes.ArchiveNamedElementSuccess: {
        // Remove element blocks on archived named elements
        if (!state) return null;
        return {
          ...state,
          elementBlocks: state.elementBlocks.filter(
            b => b.namedElementId !== action.data.namedElement?.namedElement?.namedElement?.id,
          ),
        };
      }
    }

    return state;
  },
});

const initialRecordingFeatures: RecordingFeatures = {
  enabled: false,
  consoleWatcher: false,
  ajaxWatcher: false,
  resourceUploading: false,
  recordingShutoff: false,
  metadata: null,
  trafficFiltering: 0,
  clientSideRageClick: false,
  recordingActive: RecordingActiveFor.REC_UNSET,
  mobileImageUnmaskMode: AutomaticImageUnmaskMode.UNMASK_MODE_OFF,
  keepRecordingType: KeepRecordingType.KEEP_RECORDING_DISABLED,
  customEndpoint: '',
  dropRate: 0,
  canvasWatcherMode: CanvasWatcherMode.DISABLED,
};

type RecordingFeaturesState = {
  loading: boolean;
  error: boolean;
  data: RecordingFeatures;
};

const recordingFeatures = combineReducers<RecordingFeaturesState>({
  loading: function loading(state: boolean = false, action: IngestionSettingsActions): boolean {
    switch (action.type) {
      case IngestionSettingsTypes.FetchRecordingFeaturesRequest:
      case IngestionSettingsTypes.UpdateClientSideRageClickRequest:
        return true;
      case IngestionSettingsTypes.FetchRecordingFeaturesSuccess:
      case IngestionSettingsTypes.FetchRecordingFeaturesFailure:
      case IngestionSettingsTypes.UpdateClientSideRageClickSuccess:
      case IngestionSettingsTypes.UpdateClientSideRageClickFailure:
        return false;
    }

    return state;
  },
  error: function error(state: boolean = false, action: IngestionSettingsActions): boolean {
    switch (action.type) {
      case IngestionSettingsTypes.FetchRecordingFeaturesRequest:
      case IngestionSettingsTypes.FetchRecordingFeaturesSuccess:
      case IngestionSettingsTypes.UpdateClientSideRageClickRequest:
      case IngestionSettingsTypes.UpdateClientSideRageClickSuccess:
        return false;
      case IngestionSettingsTypes.FetchRecordingFeaturesFailure:
      case IngestionSettingsTypes.UpdateClientSideRageClickFailure:
        return true;
    }

    return state;
  },
  data: function data(
    state: RecordingFeatures = initialRecordingFeatures,
    action: IngestionSettingsActions,
  ): RecordingFeatures {
    switch (action.type) {
      case IngestionSettingsTypes.FetchRecordingFeaturesSuccess:
        return action.data.versions[0] ?? state;
      case IngestionSettingsTypes.UpdateClientSideRageClickSuccess:
        if (!action.request.body) return state;
        return {
          ...state,
          clientSideRageClick: !!action.request.body.clientSideRageClick,
        };
    }
    return state;
  },
});

const keepRules = createRequestReducer<KeepRuleSetWithId | null>()(
  IngestionSettingsTypes.FetchKeepRulesRequest,
  IngestionSettingsTypes.FetchKeepRulesSuccess,
  IngestionSettingsTypes.FetchKeepRulesFailure,
);

const detectionsConfigRules = createRequestReducer<GetDetectionsConfigurationRulesResponse>()(
  IngestionSettingsTypes.FetchDetectionsConfigRulesRequest,
  IngestionSettingsTypes.FetchDetectionsConfigRulesSuccess,
  IngestionSettingsTypes.FetchDetectionsConfigRulesFailure,
);

type IngestionSettingsReducerState = {
  behaviorSignalSettings: ReturnType<typeof behaviorSignalSettings>;
  recordingFeatures: ReturnType<typeof recordingFeatures>;
  keepRules: ReturnType<typeof keepRules>;
  detectionsConfigRules: ReturnType<typeof detectionsConfigRules>;
};

export const ingestionSettingsReducer = combineReducers<IngestionSettingsReducerState>({
  behaviorSignalSettings,
  recordingFeatures,
  keepRules,
  detectionsConfigRules,
});
